import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CheckCircle2Icon, EyeIcon, HandshakeIcon, MessageCircleIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Container } from '../../components/ui/Container';
import { GeometricAccent } from '../../components/ui/GeometricAccent';
import { MosqueArchMask } from '../../components/ui/MosqueArchMask';
import { PatternDecor } from '../../components/ui/PatternDecor';
type Tone = 'sage' | 'sky' | 'rose' | 'sand';
type CoverageItem = {
  icon: typeof EyeIcon;
  title: string;
  description: string;
  tone: Tone;
};
const toneClasses: Record<Tone, string> = {
  sage: 'border-forest-700/10 bg-sage-100',
  sky: 'border-[#C7D4E0]/40 bg-[#EDF2F7]',
  rose: 'border-[#E0BFB3]/40 bg-[#F7EDE9]',
  sand: 'border-sand-300/40 bg-sand-100'
};
const coverage: CoverageItem[] = [{
  icon: EyeIcon,
  title: 'التعرّف إلى العلامات المبكرة',
  description: 'فهم أشكال التمييز الخفية وكيفية الاستماع إلى طفلك دون أن تجعله يشعر بالهلع.',
  tone: 'sage'
}, {
  icon: BookOpenIcon,
  title: 'الحقوق والمسؤوليات الأساسية',
  description: 'نظرة واضحة بلغة بسيطة إلى ما يجب على المدارس والمؤسسات تقديمه وما يمكنك توقعه.',
  tone: 'sky'
}, {
  icon: MessageCircleIcon,
  title: 'التواصل مع المؤسسات',
  description: 'كيفية طرح المخاوف بهدوء وتوثيق المواقف والتصعيد باحترام عند الحاجة.',
  tone: 'rose'
}, {
  icon: HandshakeIcon,
  title: 'الحل لا المواجهة',
  description: 'الهدف هو استعادة الكرامة وحماية طفلك، لا الفوز في نزاع. التواصل الهادئ هو الموقف الأقوى.',
  tone: 'sand'
}];
const responseSteps = [{
  number: '01',
  title: 'لاحظ',
  description: 'استمع إلى طفلك بهدوء. دوّن ما حدث ومتى ومن كان حاضرًا، وحافظ على اتزانك.'
}, {
  number: '02',
  title: 'وثّق',
  description: 'اكتب التفاصيل بوضوح وواقعية: الصور والرسائل والأسماء والتواريخ. تجنّب اللغة العاطفية.'
}, {
  number: '03',
  title: 'تواصل',
  description: 'تحدث مع المدرسة أو المؤسسة مباشرة. أحضر التوثيق وابقَ هادئًا ومركزًا على الحل.'
}, {
  number: '04',
  title: 'صعّد عند الحاجة',
  description: 'عند الضرورة، صعّد باحترام إلى مسؤولين أعلى أو جهات حوكمة أو قنوات الشكوى الرسمية.'
}];
const principles = ['الوقاية أولًا: فالوعي المبكر يقلّل الضرر', 'حماية كرامة طفلك فوق كل اعتبار', 'التعامل مع المؤسسات بهدوء لا بعاطفة', 'التوثيق بعناية وبالوقائع', 'الحل هو الهدف، لا المواجهة'];
export function ArabicPreventingDiscrimination() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-4275" data-mp="1wjq8xohw12wg">
      <section className="relative isolate overflow-hidden" data-id="element-4276" data-mp="21fp35spwdf4s">
        <GeometricAccent variant="arabesque" className="absolute -right-20 top-6 hidden h-[420px] w-[420px] md:block" opacity={0.08} data-id="element-4277" data-mp="26ftnjttxeu0a" />
        <GeometricAccent variant="dots" className="absolute left-8 top-16 h-32 w-32" opacity={0.4} data-id="element-4278" data-mp="3cvm1g7jmlbeo" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-4279" data-mp="3hx34a8lo77xw">
          <div className="grid items-center gap-10 lg:grid-cols-12" data-id="element-4280" data-mp="166bs3xnrjqrd">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="lg:col-span-6" data-id="element-4281" data-mp="1180ino1dri9o">
              <span className="text-sm font-semibold text-forest-700" data-id="element-4282" data-mp="ri7g1cj7udhk">الحماية والوعي والاستجابة الهادئة</span>
              <h1 className="mt-3 font-display text-4xl font-semibold leading-[1.45] text-ink-900 sm:text-5xl lg:text-[3.5rem]" data-id="element-4283" data-mp="mkyzfgdpuhif">
                الوقاية من التمييز
              </h1>
              <div className="mt-6 max-w-xl space-y-3 text-base leading-loose text-ink-700 md:text-lg" data-id="element-4284" data-mp="djhkw2fyinvu">
                <p data-id="element-4285" data-mp="8lfroooth86n">
                  غالبًا ما يكون التمييز خفيًا، وقد يصعب على الأطفال شرحه. يساعد هذا البرنامج الوالدين على التعرّف إلى العلامات المبكرة وفهم الحقوق الأساسية ومسؤوليات المدارس والتواصل بفاعلية مع المؤسسات.
                </p>
                <p className="font-semibold text-ink-900" data-id="element-4286" data-mp="10kr8ki3bo912">ينصب التركيز على الوقاية والكرامة والحل، لا المواجهة.</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-4287" data-mp="14l1mcgxcw3qh">
                <a href="#content" data-id="element-4288" data-mp="qmabd3dsiy6k"><Button variant="primary" size="lg" data-id="element-4289" data-mp="qmtma050wmb3">استكشف البرنامج</Button></a>
                <Link to="/ar/parents/parenting-in-non-muslim-countries" data-id="element-4290" data-mp="vlepm8fknm3r"><Button variant="secondary" size="lg" data-id="element-4291" data-mp="vjtsqc5sbr2q">التربية في الدول غير المسلمة</Button></Link>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-4292" data-mp="ln789ohdf218">
              <div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-4293" data-mp="cssuxrgei1kr">
                <MosqueArchMask src="https://cdn.magicpatterns.com/uploads/jHGadue8Tm8CvPGACZTXbP/Discrimination5.jpg" alt="أم مسلمة وابنتها تقفان بثقة في ممر مدرسة" data-id="element-4294" data-mp="3u3si17httz5e" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section id="content" className="scroll-mt-24 bg-sage-50/60 py-16 md:py-24" data-id="element-4295" data-mp="3bh2x31226aig">
        <Container data-id="element-4296" data-mp="3pfss8b5bi046">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-4297" data-mp="3kfelcwfgcw7q">ما الذي يغطيه البرنامج</h2>
          <p className="mt-5 max-w-3xl leading-loose text-ink-700" data-id="element-4298" data-mp="2908z1wu0ubg">إرشاد عملي وهادئ ومنظّم للمواقف التي قد يواجهها معظم الوالدين المسلمين في مرحلة ما.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-4299" data-mp="3ubhx8u03w97v">
            {coverage.map((item, index) => <CoverageCard key={item.title} item={item} index={index} data-id="element-4300" data-mp="bytnaaocznje" data-mpi={index} />)}
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-4301" data-mp="lxsjuuvunsq1">
        <PatternDecor side="left" y="top" size="lg" data-id="element-4302" data-mp="lvep6n530ij0" />
        <Container data-id="element-4303" data-mp="gzr0e8uihre1">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-4304" data-mp="h0939oboj3ux">إطار للاستجابة الهادئة</h2>
          <p className="mt-5 max-w-3xl leading-loose text-ink-700" data-id="element-4305" data-mp="o01nzi54dz26">منهج بسيط قابل للتكرار عند حدوث أي موقف، مبني على الوضوح والكرامة والحل الفعّال.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4" data-id="element-4306" data-mp="xzabbs5hcffk">
            {responseSteps.map((step, index) => <ResponseStepCard key={step.number} step={step} index={index} data-id="element-4307" data-mp="12sd6g4pwnwxv" data-mpi={index} />)}
          </div>
        </Container>
      </section>

      <section className="bg-[#EDF2F7]/60 py-16 md:py-24" data-id="element-4308" data-mp="17s7xq9dleojg">
        <Container data-id="element-4309" data-mp="1bq5mxc7d5amu">
          <div className="grid items-center gap-12 lg:grid-cols-12" data-id="element-4310" data-mp="1bpnlm15ug682">
            <div className="lg:col-span-6" data-id="element-4311" data-mp="1grlbhe8r1z9z">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-4xl" data-id="element-4312" data-mp="1goqq9khwwafi">مبادئنا الأساسية</h2>
              <ul className="mt-6 space-y-3" data-id="element-4313" data-mp="3iig8uailu6it">
                {principles.map(principle => <li key={principle} className="flex items-start gap-3 leading-loose text-ink-700" data-id="element-4314" data-mp="3nghqqs4anzsp">
                    <CheckCircle2Icon size={18} className="mt-1 shrink-0 text-forest-700" strokeWidth={1.6} aria-hidden="true" data-id="element-4315" data-mp="3fy0e4d14g4vi" />
                    <span data-id="element-4316" data-mp="3b607k1qjqdpy">{principle}</span>
                  </li>)}
              </ul>
            </div>
            <div className="lg:col-span-6" data-id="element-4317" data-mp="367yq1l8rqmgl">
              <div className="rounded-2xl border border-sage-200 bg-white p-7 shadow-soft md:p-8" data-id="element-4318" data-mp="318udckmmy6li">
                <p className="mb-4 text-sm font-semibold text-forest-700" data-id="element-4319" data-mp="2ri64iv41wfb">ملاحظة مهمة</p>
                <p className="leading-loose text-ink-700" data-id="element-4320" data-mp="3txj9044x9jpv">
                  يقدّم هذا البرنامج التثقيف والوعي والإرشاد العملي، ولا يمثّل خدمة تمثيل قانوني. في المسائل القانونية المحددة، يمكننا مساعدتك على فهم الوضع والتواصل مع الخدمات القانونية المناسبة.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-4321" data-mp="2dkb86dyvu9xn">
        <PatternDecor side="right" y="middle" size="md" data-id="element-4322" data-mp="28idip1zw2iw9" />
        <Container data-id="element-4323" data-mp="2y7j896nebadm">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between" data-id="element-4324" data-mp="2y710pdva99qe">
            <div className="max-w-xl" data-id="element-4325" data-mp="337f94x0sqloy">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-4326" data-mp="338gld4tjte89">كن مستعدًا، لا ردّ فعل</h2>
              <p className="mt-5 leading-loose text-ink-700" data-id="element-4327" data-mp="2t8ps16gq559r">تمنحك العضوية وصولًا مستمرًا إلى إرشاد المختصين ومنظومة دعم هادئة.</p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-4328" data-mp="3gz3nz82fjo9m">
              <Link to="/ar/waitlist" data-id="element-4329" data-mp="3lxdhzfcf4itn"><Button variant="primary" data-id="element-4330" data-mp="3lxwtad7kc8yp">انضم إلى العضوية</Button></Link>
              <Link to="/ar/parents/resources-guidance" data-id="element-4331" data-mp="386i5lt2pnjlb"><Button variant="secondary" data-id="element-4332" data-mp="385gw3t1cf55p">عرض الموارد</Button></Link>
            </div>
          </div>
        </Container>
      </section>
    </div>;
}
function CoverageCard({
  item,
  index
}: {
  item: CoverageItem;
  index: number;
}) {
  const Icon = item.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06
  }} className={`flex gap-4 rounded-2xl border p-6 shadow-soft ${toneClasses[item.tone]}`} data-id="element-4333" data-mp="8ja6or09y8ri">
      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-4334" data-mp="22zieh87jjxmc">
        <Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-4335" data-mp="1y1yz24z3xest" />
      </span>
      <div data-id="element-4336" data-mp="1ab2zumsbnhbj">
        <h3 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-4337" data-mp="1aajsg00asbbr">{item.title}</h3>
        <p className="mt-1 text-sm leading-loose text-ink-700" data-id="element-4338" data-mp="15d1efv7fb5ww">{item.description}</p>
      </div>
    </motion.article>;
}
function ResponseStepCard({
  step,
  index
}: {
  step: (typeof responseSteps)[number];
  index: number;
}) {
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.06
  }} className="rounded-2xl border border-sage-200 bg-white p-6 shadow-soft" data-id="element-4339" data-mp="2vne4xv6iotao">
      <span dir="ltr" className="font-display text-3xl font-semibold text-sand-400" data-id="element-4340" data-mp="1v5ncwsijw0s6">{step.number}</span>
      <h3 className="mt-3 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-4341" data-mp="1qex6dbye1rvw">{step.title}</h3>
      <p className="mt-2 text-sm leading-loose text-ink-700" data-id="element-4342" data-mp="1lg3shif6vyv7">{step.description}</p>
    </motion.article>;
}